import { createApp, reactive } from 'vue';
import App from './App.vue';
// import './registerServiceWorker';
import router from './router';
import store from './store';
import { createPinia } from 'pinia';
import './icons'; // icon
import { SvgIcon } from './icons'; // 引入SvgIcon组件
import ElementPlus from 'element-plus';
import 'element-plus/dist/index.css';
// 引入 Element Plus 中文语言包
import zhCn from 'element-plus/dist/locale/zh-cn.mjs';
// 单独引入消息组件以避免通过 ElementPlus.ElMessage 访问导致 undefined

import './assets/css/common.scss';
import './assets/css/main.css';
import * as echarts from 'echarts';
import mixin from './assets/js/mixin';
import api from './api/index';
import { handleApiError } from './utils/handleApiError';
import { showErrorModal, showSuccessModal } from './utils/simpleModal';

const app = createApp(App);

// 动态布局尺寸：根据当前窗口宽度计算内容区域最小宽度
// 目的：避免写死 1600px，又防止在超小屏过度压缩版面
// 策略：在 [1200, 1920] 区间内随窗口宽度变化，低于 1200 仍保持 1200，超过 1920 不再放大
function computeLayoutMinWidth() {
  const w = window.innerWidth;
  // 移动端：保持原逻辑（不强制 min-width，交给媒体查询样式）
  if (w <= 768) return 'auto';
  // 桌面端：设定一个合理区间，避免过窄/过宽
  const MIN = 1600;      // 最小内容宽度（可按需调低 / 调高）
  const MAX = w-100;      // 上限，防止超宽屏过度伸展
  const IDEAL = Math.floor(w * 0.95); // 预留一点边距（可调为 0.9 / 1.0）
  const clamped = Math.max(MIN, Math.min(MAX, IDEAL));
  return clamped + 'px';
}
// 使用 reactive 保持响应性
const layoutState = reactive({
  minWidth: computeLayoutMinWidth()
});
// 监听窗口尺寸变化
window.addEventListener('resize', () => {
  layoutState.minWidth = computeLayoutMinWidth();
});
// 向全局注入（供 inject 使用）
app.provide('layoutState', layoutState);



// 注册SvgIcon组件
app.component('svg-icon', SvgIcon);

app.config.globalProperties.$api = api;
app.config.globalProperties.$echarts = echarts;
app.config.globalProperties.$handleApiError = handleApiError;
app.config.globalProperties.$messageSuccess = function(text) {
  let message = '操作成功';
  // 英文 -> 中文词典（常见后端返回/前端提示）
  const en2zhMap = {
    'Success': '成功',
    'success': '成功',
    'Operation successful': '操作成功',
    'Operation success': '操作成功',
    'Add success': '添加成功',
    'Added successfully': '添加成功',
    'Update success': '更新成功',
    'Updated successfully': '更新成功',
    'Delete success': '删除成功',
    'Deleted successfully': '删除成功',
    'Copy success': '复制成功',
    'Copied successfully': '复制成功',
    'Password updated successfully': '修改密码成功',
    'Login successful': '登录成功',
    'Logout successful': '退出成功',
    'Set successfully': '设置成功',
    'Saved successfully': '保存成功'
  };
  
  // 完全安全的消息提取
  try {
    if (!text) {
      message = '操作成功';
    } else if (typeof text === 'string') {
      message = text;
    } else if (typeof text === 'object') {
      if (text.hasOwnProperty('message') && typeof text.message === 'string') {
        message = text.message;
      } else if (text.hasOwnProperty('msg') && typeof text.msg === 'string') {
        message = text.msg;
      } else {
        message = String(text);
      }
    } else {
      message = String(text);
    }
  } catch (e) {
    console.warn('Error processing success message:', e);
    message = '操作成功';
  }
  
  // 确保 message 是有效的字符串
  if (!message || typeof message !== 'string') {
    message = '操作成功';
  }
  // 如果包含英文且命中词典，转换
  if (/^[\x00-\x7F]+$/.test(message.trim())) { // 仅 ASCII（可能是英文）
    const direct = en2zhMap[message.trim()];
    if (direct) message = direct;
  }
  
  showSuccessModal(message);
};
app.config.globalProperties.$messageError = function(text) {
  let message = '发生未知错误';
  const enErrMap = {
    'Error': '错误',
    'Unknown error': '未知错误',
    'Server Error': '服务器错误',
    'Network Error': '网络错误',
    'Request failed': '请求失败',
    'Request timeout': '请求超时',
    'Timeout': '请求超时',
    'Unauthorized': '未授权',
    'Forbidden': '没有权限',
    'Permission denied': '没有权限',
    'Not Found': '未找到',
    'Invalid parameters': '参数无效',
    'Invalid parameter': '参数无效',
    'Invalid request': '无效请求',
    'Too many requests': '请求过于频繁',
    'Failed': '失败'
  };
  
  // 完全安全的错误信息提取
  try {
    if (!text) {
      message = '发生未知错误';
    } else if (typeof text === 'string') {
      message = text;
    } else if (typeof text === 'object') {
      // 使用 hasOwnProperty 和 in 操作符进行安全检查
      if (text.hasOwnProperty('message') && typeof text.message === 'string') {
        message = text.message;
      } else if (text.hasOwnProperty('msg') && typeof text.msg === 'string') {
        message = text.msg;
      } else if ('data' in text && text.data && 'message' in text.data && typeof text.data.message === 'string') {
        message = text.data.message;
      } else if ('response' in text && text.response && 'data' in text.response && text.response.data && 'message' in text.response.data && typeof text.response.data.message === 'string') {
        message = text.response.data.message;
      } else if ('error' in text && typeof text.error === 'string') {
        message = text.error;
      } else {
        // 如果是对象但没有识别的属性，尝试转换为字符串
        message = String(text);
      }
    } else {
      // 如果不是字符串也不是对象，转换为字符串
      message = String(text);
    }
  } catch (e) {
    // 如果任何步骤出错，使用默认消息
    console.warn('Error processing error message:', e);
    message = '发生未知错误';
  }
  
  // 确保 message 是有效的字符串
  if (!message || typeof message !== 'string') {
    message = '发生未知错误';
  }
  if (/^[\x00-\x7F]+$/.test(message.trim())) { // 全英文尝试翻译
    const direct = enErrMap[message.trim()];
    if (direct) message = direct;
  }
  
  showErrorModal(message);
};

app.mixin(mixin);
// 使用中文 locale
app.use(ElementPlus, { locale: zhCn });
app.use(store);
app.use(createPinia());
app.use(router);
app.mount('#app');

